'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import type React from 'react';
import { cn } from '../../utils/cn';
import { IconCautionCircleFilled } from '../icons/IconCautionCircleFilled.ts';
import { IconCheckCircleFilled } from '../icons/IconCheckCircleFilled.ts';
import { IconCloseCircleFilled } from '../icons/IconCloseCircleFilled.ts';
import { IconInformationCircleFilled } from '../icons/IconInformationCircleFilled.ts';
import { IconInformationCircleStroke } from '../icons/IconInformationCircleStroke.ts';

export interface HelperTextProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof helperTextVariants> {
    children: React.ReactNode;
    hideIcon?: boolean;
}

const helperTextVariants = cva('inline-flex items-center gap-lr-spacing-050 transition-colors', {
    variants: {
        size: {
            sm: 'text-lr-body-small-light',
            md: 'text-lr-body-balanced-light'
        },
        variant: {
            default: ['text-lr-text-main'],
            success: ['text-lr-text-success'],
            information: ['text-lr-text-info'],
            warning: ['text-lr-text-warning'],
            error: ['text-lr-text-error'],
            subtle: ['text-lr-text-subtle']
        }
    },
    defaultVariants: {
        size: 'md',
        variant: 'default'
    }
});

export const iconVariants = cva(['flex-shrink-0'], {
    variants: {
        variant: {
            default: 'text-lr-icon-main',
            success: 'text-lr-icon-success',
            information: 'text-lr-icon-info',
            warning: 'text-lr-icon-warning',
            error: 'text-lr-icon-error',
            subtle: 'text-lr-icon-subtle'
        }
    },
    defaultVariants: {
        variant: 'default'
    }
});

function getIconForVariant(variant: HelperTextProps['variant']) {
    switch (variant) {
        case 'success':
            return IconCheckCircleFilled;
        case 'information':
            return IconInformationCircleFilled;
        case 'warning':
            return IconCautionCircleFilled;
        case 'error':
            return IconCloseCircleFilled;
        case 'default':
        case 'subtle':
        case null:
        case undefined:
        default:
            return IconInformationCircleStroke;
    }
}

function HelperText({ className, size, variant, children, hideIcon, ...rest }: HelperTextProps) {
    const IconComponent = getIconForVariant(variant);

    return (
        children && (
            <div className={cn(helperTextVariants({ size, variant }), className)} {...rest}>
                {!hideIcon && <IconComponent className={cn(iconVariants({ variant }))} size={'xs'} />}{' '}
                <span>{children}</span>
            </div>
        )
    );
}

export { HelperText };
export default HelperText;
